"use client";

import { Badge } from "@workspace/ui/components/badge";
import { cn } from "@workspace/ui/lib/utils";
import { motion } from "framer-motion";
import {
  CalendarIcon,
  HashIcon,
  ImageIcon,
  SparklesIcon,
  TagIcon,
} from "lucide-react";
import type { MappedRequest } from "../ImagesPage";

type DetailPanelInfoProps = {
  request: MappedRequest;
};

const STAGE_COLORS: Record<string, string> = {
  collecting:
    "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200",
  ready:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200",
  processing:
    "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200",
  completed:
    "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200",
  failed: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200",
  expired: "bg-zinc-100 text-zinc-800 dark:bg-zinc-800/30 dark:text-zinc-300",
  rejected:
    "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200",
};

const CONTAINER_VARIANTS = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.03 },
  },
};

const ITEM_VARIANTS = {
  hidden: { opacity: 0, x: -4 },
  visible: { opacity: 1, x: 0 },
};

export function DetailPanelInfo({ request }: DetailPanelInfoProps) {
  const createdDate = new Date(request.createdAt);
  const formattedDateTime = createdDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <motion.div
      animate="visible"
      className="flex h-full flex-col overflow-y-auto scrollbar-hide"
      initial="hidden"
      variants={CONTAINER_VARIANTS}
    >
      {request.previewUrl && (
        <motion.div className="px-4 pt-4 pb-3" variants={ITEM_VARIANTS}>
          <div className="overflow-hidden rounded-lg border border-zinc-200/50 dark:border-zinc-700/50">
            <img
              alt="Preview"
              className="w-full max-h-48 object-cover"
              src={request.previewUrl}
            />
          </div>
        </motion.div>
      )}

      <div className="flex-1 px-4 pb-4">
        <div className="divide-y divide-zinc-100 dark:divide-zinc-800/50">
          <InfoRow
            icon={<SparklesIcon className="size-3.5" />}
            label="Prompt"
            value={request.promptText || "No prompt"}
          />

          <InfoRow
            icon={<TagIcon className="size-3.5" />}
            label="Status"
            value={
              <Badge
                className={cn(
                  "h-5 px-1.5 text-[10px] font-medium capitalize",
                  STAGE_COLORS[request.stage] || STAGE_COLORS.expired
                )}
                variant="secondary"
              >
                {request.stage}
              </Badge>
            }
          />

          <InfoRow
            icon={<CalendarIcon className="size-3.5" />}
            label="Created"
            value={formattedDateTime}
          />

          <InfoRow
            icon={<HashIcon className="size-3.5" />}
            label="ID"
            value={
              <code className="font-mono text-[10px] text-muted-foreground">
                {request._id.slice(0, 12)}...
              </code>
            }
          />

          {request.previewUrl && (
            <InfoRow
              icon={<ImageIcon className="size-3.5" />}
              label="Image"
              value={
                <a
                  className="text-xs text-blue-600 hover:underline dark:text-blue-400"
                  href={request.previewUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  View full size
                </a>
              }
            />
          )}
        </div>
      </div>
    </motion.div>
  );
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <motion.div
      className="flex items-center justify-between py-2.5"
      variants={ITEM_VARIANTS}
    >
      <div className="flex items-center gap-2 text-muted-foreground">
        {icon}
        <span className="text-xs font-medium">{label}</span>
      </div>
      <div className="text-xs text-foreground max-w-[60%] text-right truncate">
        {value}
      </div>
    </motion.div>
  );
}
